import * as XLSX from 'xlsx';
import { differenceInMinutes } from 'date-fns';

import { formatDate, formatMinutesToHours } from '../date';
import { IRouteEventReportData } from '@src/app/interfaces/report-route.interface';
import { NewDataStoreService } from '@shared/services/new-data-store.service';
import { removePolishDiacritics } from '../remove-polish-diacritics';
import { TranslateService } from '@ngx-translate/core';

export interface XLSRowBuilder<T> {
  buildRow: (entry: T) => Object,
}

export class ReportRowBuilder implements XLSRowBuilder<IRouteEventReportData> {
  constructor(public newDataStoreService: NewDataStoreService, public translateService: TranslateService) {}

  public buildRow(entry: IRouteEventReportData) {
    const startLocation = `${entry?.D?.S?.Cr}, ${entry?.D?.S?.Ct}, ${entry?.D?.S?.St}`;
    const endLocation = `${entry?.D?.E?.Cr}, ${entry?.D?.E?.Ct}, ${entry?.D?.E?.St}`;
    const distance = entry.D.D;
    const rideTime = ReportRowBuilder.calculateDuration(entry.St, entry.En);
    const stopTime = entry.stopTimeInMinutes <= 1 ? '<1min' : formatMinutesToHours(entry.stopTimeInMinutes);
    const driverData = entry.Dr && this.newDataStoreService.getDriverById(entry.Dr);
    const driver = driverData ? `${ driverData?.FN || '-' } ${ driverData?.LN || '-' }` : this.translateService.instant('noDriverData');

    return {
      [this.translateService.instant('timeFrom')]: formatDate('dd.MM.yyyy HH:mm:ss', new Date(entry.St)),
      [this.translateService.instant('timeTo')]: entry.En ? formatDate('dd.MM.yyyy HH:mm:ss', new Date(entry.En)) : this.translateService.instant('during'),
      [this.translateService.instant('start')]: removePolishDiacritics(startLocation),
      [this.translateService.instant('end')]: removePolishDiacritics(endLocation),
      [this.translateService.instant('distanceKM')]: distance,
      [this.translateService.instant('timeDrive')]: rideTime,
      [this.translateService.instant('reports.stopTime')]: entry.stopTimeInMinutes !== null ? stopTime : this.translateService.instant('noDataStop'),
      [this.translateService.instant('driver')]: removePolishDiacritics(driver),
    };
  }

  public static calculateDuration(start: Date, end: Date): string {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return formatMinutesToHours(differenceInMinutes(endDate, startDate));
  }
}

export class GenerateXlsFileReport<T> {
  private data: T[];

  constructor(data: T[], public reportRowBuilder: XLSRowBuilder<T>) {
    this.data = data;
  }

  public generateReport(fileName = 'raport'): void {
    const rows = this.data.map(entry => this.reportRowBuilder.buildRow(entry));
    const worksheet = XLSX.utils.json_to_sheet(rows);

    const headers = Object.keys(rows[0]);
    worksheet['!cols'] = headers.map(header => ({ wch: 30 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Raport');
    XLSX.writeFile(workbook, (fileName + '.xlsx'), { type: 'array' });
  }
}
